import React, { useState, useRef, useEffect } from 'react';

import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const SupportCenter = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [faqSearchTerm, setFaqSearchTerm] = useState('');
  const [faqCategories, setFaqCategories] = useState([]);
  const [faqs, setFaqs] = useState({});
  const chatIframeRef = useRef(null);

  // Fetch FAQs from database
  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const { data: cats, error: catError } = await supabase
          .from('faq_categories')
          .select('*')
          .order('display_order', { ascending: true });
        
        if (catError) throw catError;
        
        const { data: items, error: faqError } = await supabase
          .from('faqs')
          .select('*')
          .eq('is_published', true)
          .order('display_order', { ascending: true });

        if (faqError) throw faqError;

        if (cats && cats.length > 0) {
            setFaqCategories(cats);
            if (!cats.some(c => c.id === activeCategory)) {
               setActiveCategory(cats[0].id);
            }
        }
        
        if (items) {
            const groupedFaqs = {};
            cats.forEach(c => groupedFaqs[c.id] = []);
            items.forEach(item => {
                if (!groupedFaqs[item.category_id]) groupedFaqs[item.category_id] = [];
                groupedFaqs[item.category_id].push({
                    question: item.question,
                    answer: item.answer
                });
            });
            setFaqs(groupedFaqs);
        }
      } catch (e) {
        console.error('Error fetching FAQs:', e);
      }
    };
    fetchFAQs();
  }, []);

  // Iframe message listener
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'chat-ready') {
        const postSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (chatIframeRef.current?.contentWindow) {
                chatIframeRef.current.contentWindow.postMessage({ type: 'supabase-session', payload: session || null }, '*');
            }
        };
        postSession();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const contactChannels = [
    {
      title: 'Email Support',
      description: 'Get help via email within 24 hours',
      icon: 'Mail',
      contact: 'support@opengadget.com',
      availability: '24/7'
    },
    {
      title: 'Live Chat',
      description: 'Chat with our support team in real-time',
      icon: 'MessageCircle',
      contact: 'Start Chat',
      availability: 'Mon-Fri, 9AM-6PM Central'
    },
    {
      title: 'Phone Support',
      description: 'Speak directly with a support specialist',
      icon: 'Phone',
      contact: '+1 (555) 123-4567',
      availability: 'Mon-Fri, 9AM-6PM EST'
    },
    {
      title: 'Community Forum',
      description: 'Connect with other users and experts',
      icon: 'Users',
      contact: 'Visit Forum',
      availability: 'Always Active'
    }
  ];

  const systemStatus = [
    { service: 'API Services', status: 'operational', uptime: '99.99%' },
    { service: '3D Rendering', status: 'operational', uptime: '99.95%' },
    { service: 'File Storage', status: 'operational', uptime: '100%' },
    { service: 'Authentication', status: 'operational', uptime: '99.98%' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <Header />
      {/* Hero Section */}
      <div className="container mx-auto px-6 pt-24 pb-16">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Support Center
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            We're here to help. Get instant answers or reach out to our support team.
          </p>
        </div>

        {/* Contact Channels */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactChannels?.map((channel, index) => (
            <div
              key={index}
              onClick={() => { 
                if (channel.title === 'Live Chat') {
                  window.dispatchEvent(new CustomEvent('open-support-chat'));
                }
              }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 cursor-pointer group"
            >
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl w-fit mb-4 group-hover:scale-110 transition-transform">
                <Icon name={channel?.icon} className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">{channel?.title}</h3>
              <p className="text-sm text-slate-600 mb-3">{channel?.description}</p>
              <div className="text-blue-600 font-semibold mb-2">{channel?.contact}</div>
              <div className="text-xs text-slate-500">{channel?.availability}</div>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden mb-16">
          <div className="p-8 border-b border-slate-200">
            <h2 className="text-3xl font-bold text-slate-800 mb-2">Frequently Asked Questions</h2>
            <p className="text-slate-600 mb-6">Find quick answers to common questions</p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl">
              <Icon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search for help articles, FAQs, guides..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                value={faqSearchTerm}
                onChange={(e) => setFaqSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {/* Category Tabs */}
          {!faqSearchTerm && (
            <div className="flex border-b border-slate-200 px-8 overflow-x-auto no-scrollbar">
              {faqCategories?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() => setActiveCategory(category?.id)}
                  className={`flex items-center space-x-2 px-6 py-4 border-b-2 transition-colors whitespace-nowrap ${
                    activeCategory === category?.id
                      ? 'border-blue-600 text-blue-600' :'border-transparent text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Icon name={category?.icon} size={18} />
                  <span className="font-medium">{category?.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* FAQ Items */}
          <div className="p-8">
            <div className="space-y-6">
              {faqSearchTerm ? (
                // Search Results View
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Search Results</h3>
                    <button 
                      onClick={() => setFaqSearchTerm('')}
                      className="text-blue-600 text-sm hover:underline"
                    >
                      Clear Search
                    </button>
                  </div>
                  {Object.entries(faqs).flatMap(([catId, items]) => 
                    items
                      .filter(faq => faq.question.toLowerCase().includes(faqSearchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(faqSearchTerm.toLowerCase()))
                      .map(faq => ({ ...faq, catName: faqCategories.find(c => c.id === catId)?.name }))
                  ).length > 0 ? (
                    Object.entries(faqs).flatMap(([catId, items]) => 
                      items
                        .filter(faq => faq.question.toLowerCase().includes(faqSearchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(faqSearchTerm.toLowerCase()))
                        .map((faq, index) => (
                          <div key={`${catId}-${index}`} className="border-b border-slate-200 last:border-0 pb-6 last:pb-0">
                            <div className="text-[10px] font-bold text-blue-500 uppercase mb-1">{faqCategories.find(c => c.id === catId)?.name}</div>
                            <h4 className="text-lg font-semibold text-slate-800 mb-3 flex items-start">
                              <Icon name="HelpCircle" size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" />
                              {faq?.question}
                            </h4>
                            <p className="text-slate-600 ml-7">{faq?.answer}</p>
                          </div>
                        ))
                    )
                  ) : (
                    <div className="text-center py-12">
                      <Icon name="Search" size={48} className="mx-auto text-slate-200 mb-4" />
                      <p className="text-slate-500 text-lg">No FAQs found matching "{faqSearchTerm}"</p>
                      <p className="text-slate-400 text-sm">Try using different keywords or browse categories.</p>
                    </div>
                  )}
                </>
              ) : (
                // Tabbed Category View
                <>
                  {faqs?.[activeCategory]?.map((faq, index) => (
                    <div key={index} className="border-b border-slate-200 last:border-0 pb-6 last:pb-0">
                      <h4 className="text-lg font-semibold text-slate-800 mb-3 flex items-start">
                        <Icon name="HelpCircle" size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" />
                        {faq?.question}
                      </h4>
                      <p className="text-slate-600 ml-7">{faq?.answer}</p>
                    </div>
                  ))}
                  {(!faqs?.[activeCategory] || faqs?.[activeCategory]?.length === 0) && (
                    <div className="text-center py-8 text-slate-500">No FAQs available in this category yet.</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 mb-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-slate-800 mb-2">System Status</h2>
              <p className="text-slate-600">All systems operational</p>
            </div>
            <div className="flex items-center space-x-2 text-green-600">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-semibold">All Systems Operational</span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {systemStatus?.map((system, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-6 rounded-xl border border-slate-200 hover:border-green-300 transition-all"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <h4 className="font-semibold text-slate-800">{system?.service}</h4>
                    <p className="text-sm text-green-600 capitalize">{system?.status}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-800">{system?.uptime}</div>
                  <p className="text-xs text-slate-500">uptime</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit Ticket CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-center text-white">
          <Icon name="Ticket" size={48} className="mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Still Need Help?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Can't find what you're looking for? Submit a support ticket and our team will get back to you within 24 hours.
          </p>
          <Button 
            variant="default" 
            className="bg-white text-blue-600 hover:bg-blue-50" 
            iconName="Send" 
            iconPosition="left"
            onClick={() => setIsChatOpen(true)}
          >
            Submit Support Ticket
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SupportCenter;